/**
 * Rejection-safe clipboard helpers.
 *
 * `navigator.clipboard.writeText`/`write` return promises that REJECT (not
 * throw synchronously) when the browser refuses the write — most notably
 * Firefox, which throws `NotAllowedError: "Clipboard write is not allowed."`
 * when the write isn't backed by transient user activation or the document
 * isn't focused. A bare `clipboard.writeText(x)` whose rejection is neither
 * awaited nor `.catch()`-ed escapes as an *unhandled promise rejection*, which
 * our global handler reports as a spurious `[AutoErrorReport]` crash.
 *
 * These helpers await the write inside a try/catch and return whether it
 * succeeded, so callers can surface a toast instead of leaking a rejection.
 */

/** Copy plain text. Resolves to `true` on success, `false` if the write failed. */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  if (!navigator.clipboard) {
    return false;
  }
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Copy rich HTML alongside a derived plain-text flavor so paste targets can
 * choose. Falls back to a plain-text write when `ClipboardItem`/rich writes are
 * unsupported. Resolves to `true` on success, `false` if every attempt failed.
 */
export async function copyHtmlToClipboard(html: string): Promise<boolean> {
  if (!navigator.clipboard) {
    return false;
  }
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  const plainText = tempDiv.textContent || tempDiv.innerText || "";

  if (typeof ClipboardItem !== "undefined" && navigator.clipboard.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([plainText], { type: "text/plain" }),
        }),
      ]);
      return true;
    } catch {
      // Fall through to a plain-text write (e.g. rich write blocked/unsupported).
    }
  }
  return copyTextToClipboard(plainText);
}
